import { abtastyService } from '@/services';
import Ribbon from '@components/tailwind/Ribbon';
import WebImage from '@components/tailwind/WebImage';
import { defaultAttributeCompare } from '@features/pdp/attribute-sorting';
import { ShopItemGroupItem } from '@types';
import withQs from '@utils/withQs';
import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import ColorSwatches from '../../../../components/ColorSwatches';
import StarRating from '../../../../components/StarRating';
import { ButtonVariants } from '../../../../components/tailwind/Button';
import { formatCurrency } from '../../../../utils/formatters.util';
import { keepPreviousData } from '@tanstack/react-query';
import useItemQuery from '../../hooks/useItemQuery';
import ProductCardFeatures from './Features';
import cx from '@utils/cx.util';
import Slider from 'react-slick';
import ColorSwatch from '@components/ColorSwatch';
import { BsArrowLeft, BsArrowRight } from 'react-icons/bs';
import ColorSwatchStyles from '@components/ColorSwatchStyles';
import { Link } from '@components/Link';
import { LoadingSpinner } from '@components/tailwind';

type Props = {
  item: ShopItemGroupItem;
  features?: string[];
  displayPosition: number;
  buttonVariant?: ButtonVariants;
  buttonText?: string;
  appendUrl?: string;
  activeColors?: number[];
  activeColorGroups?: any[];
  siteId?: number;
  useVariantUrl?: boolean;
  maxSwatchesShown?: number;
  swatchesSliderThreshold?: number;
  swatchesSliderSlidesToScroll?: number;
  className?: string;
};

const MAX_SWATCHES_SHOWN = 7;
const SWATCHES_SLIDER_THRESHOLD = 8;
const SWATCHES_SLIDER_SLIDES_TO_SCROLL = 4;

const ProductCard = ({
  item,
  features,
  displayPosition,
  swatchesSliderThreshold = SWATCHES_SLIDER_THRESHOLD,
  swatchesSliderSlidesToScroll = SWATCHES_SLIDER_SLIDES_TO_SCROLL,
  maxSwatchesShown = MAX_SWATCHES_SHOWN,
  activeColors = [],
  activeColorGroups = [],
  siteId = -1,
  className = '',
  useVariantUrl = false,
}: Props) => {
  const sliderRef = useRef<Slider>(null);
  const [activeItemUrl, setActiveItemUrl] = useState(null);
  const [activeItem, setActiveItem] = useState<any>(item);
  const { isSuccess, data: pdpItem, isFetching } = useItemQuery({
    url: activeItemUrl,
    queryOptions: {
      refetchOnWindowFocus: false,
      notifyOnChangeProps: ['data'],
      placeholderData: keepPreviousData,
    },
  });

  useEffect(() => {
    if (isSuccess && pdpItem) {
      setActiveItem(pdpItem);
    }
  }, [isSuccess, pdpItem]);

  useEffect(() => {
    if (item.isBundle) return;

    const variants = item.pickerVariants;

    const colorGroupColors = activeColorGroups
      .concat([])
      .reverse()
      .flatMap((g) => g.config.colorIds);

    const wanted = colorGroupColors.concat(activeColors.concat([]).reverse()); // reverse edits in place, no create a copy... supposedly `toReversed()` exists but it won't let me
    const available = wanted.map((id) => variants.colors.find((c) => c.id === id)).filter((c) => !!c);

    const color = available[0];

    // activeItemUrl isn't included in hook dependency on purpose
    // it allows the use to override the active swatch on a card
    if (color?.url !== activeItemUrl) {
      if (!!color) {
        setActiveItemUrl(color.url);
      } else {
        setActiveItemUrl(null);
        setActiveItem(null);
      }
    }
  }, [activeColors, activeColorGroups, item]);

  const {
    currentRateFormatted,
    ourPriceFormatted,
    isDiscounted,
    discountBanner,
    discountBubble,
    displayName,
    itemOnSale,
    rating,
    reviewCount,
    itemUrl,
    isLimitedEdition,
    isNew,
    tagline,
    canMiyo,
    itemId,
    activeSize,
    activeColor,
  } = useMemo(() => {
    const currentRateFormatted =
      activeItem?.salePriceFormatted ??
      activeItem?.details?.rateFormatted ??
      item?.salePriceFormatted ??
      item?.rateFormatted;
    const ourPriceFormatted = activeItem?.details?.ourPrice
      ? formatCurrency(activeItem?.details?.ourPrice)
      : item.ourPriceFormatted;

    const itemOnSale = activeItem?.details?.isOnSale ?? item.isOnSale;

    return {
      itemId: activeItem?.details?.id || item?.id,
      currentRateFormatted,
      ourPriceFormatted,
      displayName: activeItem?.details?.displayNameOnPage ?? item.displayName,
      isDiscounted:
        activeItem?.maxAdditionalSalePercent ||
        activeItem?.details?.salePrice ||
        activeItem?.salePrice ||
        item?.maxAdditionalSalePercent ||
        item?.salePrice,
      discountBanner:
        activeItem?.details?.discountBanner || activeItem?.discountBanner || item?.discountBanner,
      discountBubble:
        activeItem?.details?.discountBubble || activeItem?.discountBubble || item?.discountBubble,
      isLimitedEdition: activeItem?.details?.isLimitedEdition ?? item?.isLimitedEdition ?? false,
      isNew: activeItem?.details?.isNew ?? item?.isNew ?? false,
      itemOnSale,
      itemUrl: activeItem?.details?.url ?? (useVariantUrl ? item.variantUrl : item.url),
      rating: activeItem?.details?.rating?.rating ?? item?.reviewSummary?.yotpoRating ?? 0,
      reviewCount: activeItem?.details?.rating?.count ?? item?.reviewSummary?.yotpoCount ?? 0,
      tagline: activeItem?.details?.tagline ?? item?.item?.tagline ?? item.tagline,
      canMiyo: activeItem?.canMiyo ?? item?.canMiyo,
      activeSize: activeItem?.variants?.activeSize,
      activeColor:
        activeItem?.variants?.activeColor ??
        item.pickerVariants?.colors.find(
          (color) =>
            getColorFromUrl(color.url.toLowerCase()) === getColorFromUrl(item.variantUrl.toLowerCase())
        ),
    };
  }, [activeItem, item, useVariantUrl]);

  const sortedColors = useMemo(() => {
    const colorGroupColors = activeColorGroups
      .concat([])
      .reverse()
      .flatMap((g) => g.config.colorIds);

    // Show all swatches on cards as in-stock, like in the Custom Shop
    const adjustedColors = (item.pickerVariants?.colors || []).filter((c) => c.isInStock);

    return adjustedColors.toSorted(
      (a, b) =>
        colorGroupColors.indexOf(b.id) - colorGroupColors.indexOf(a.id) || defaultAttributeCompare(a, b)
    );
  }, [item.pickerVariants?.colors, activeColorGroups]);

  useEffect(() => {
    if (activeColor && sliderRef.current) {
      sliderRef.current?.slickGoTo(
        sortedColors.findIndex((c) => c.id === activeColor.id),
        true
      );
    }
  }, [activeColor, sortedColors, sliderRef.current]);

  const onSwatchClick = (swatchData) => (e) => {
    e.stopPropagation();

    abtastyService.trackClick('Product Card: Color Swatch Click');

    if (item.isBundle) {
      window.location.href = itemUrl;
    } else {
      setActiveItemUrl(swatchData.url);
    }
  };

  const onSliderSwatchClick = (index) => (swatchData) => (e) => {
    sliderRef.current?.slickGoTo(index);
    onSwatchClick(swatchData)(e);
  };

  const onSizeSwatchClick = (sizeData) => (e) => {
    e.preventDefault();
    e.stopPropagation();

    abtastyService.trackClick('Product Card: Size Swatch Click');

    if (item.isBundle) {
      window.location.href = itemUrl;
    } else {
      setActiveItemUrl(sizeData.url);
    }
  };

  const trackCardClick = (e) => {
    try {
      if (!!window.abtasty) {
        window.abtasty.send('event', {
          ec: 'Action Tracking',
          ea: 'Product Card Click',
          el: displayName,
        });
      }
    } catch (e) {
      // do nothing
    }
  };

  const repeatedColors = useMemo(() => {
    const colors = [...sortedColors];

    if (!colors || colors.length === 0) return [];

    // double until it is twice the length of the visible swatches
    while (colors.length < swatchesSliderSlidesToScroll * 3) {
      colors.push(...sortedColors);
    }
    return colors;
  }, [sortedColors, swatchesSliderSlidesToScroll]);

  return (
    <div
      className={cx(
        'tw-relative tw-h-full tw-w-full tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-p-4 hover:tw-text-brand-black hover:!tw-no-underline',
        className
      )}
      onClick={trackCardClick}>
      {isFetching && <LoadingSpinner small={true} />}
      <div className='tw-absolute tw-left-4 tw-top-2 tw-z-10'>
        {!!discountBubble?.primaryText && itemOnSale ? (
          <Ribbon>{discountBubble.primaryText}</Ribbon>
        ) : isNew ? (
          <Ribbon backgroundColor='#4195B3'>New</Ribbon>
        ) : (
          isLimitedEdition && (
            <Ribbon backgroundColor='rgb(249,115,22)' padding='4px 10px'>
              <span className='tw-whitespace-nowrap'>Limited Edition</span>
            </Ribbon>
          )
        )}
      </div>

      {canMiyo && (
        <div className='tw-absolute tw-right-4 tw-top-1 tw-z-10'>
          <Link
            onClick={(e) => {
              e.stopPropagation();

              abtastyService.trackClick('Product Card: MyRTIC Click');
            }}
            to={withQs(itemUrl, '&design=true')}
            className='tw-inline-flex tw-flex-col tw-items-center tw-justify-center tw-gap-1 tw-text-base tw-font-normal tw-text-[#242424] tw-transition hover:tw-text-brand-black hover:!tw-no-underline'>
            <span className='tw-block tw-text-xs'>Personalize with</span>
            <img src='/assets/images/rtic/myRTIC-Logo.png' alt='MyRTIC Logo' className='tw-h-[18px]' />
          </Link>
        </div>
      )}

      <div className='tw-mb-3 tw-mt-5'>
        {item.isBundle && item?.webImages?.views?.length > 1 ? (
          <Link to={itemUrl} className='tw-h-[240px] tw-no-underline'>
            <WebImage
              webImage={item.webImages.default.active}
              alt={item.displayName}
              className={classNames('tw-mx-auto', {
                'tw-max-h-[160px]': item.webImages.views.length > 1,
                'tw-max-h-full': item.webImages.views.length <= 1,
              })}
            />
            <div className='tw-mt-2.5 tw-flex tw-justify-center'>
              {item.webImages.views.slice(1).map((img, index) => (
                <div key={`bundle-image.${index}`} className='tw-m-1 tw-rounded-lg'>
                  <img className='tw-h-[70px] tw-max-w-full' src={img.active.url} alt={item.displayName} />
                </div>
              ))}
            </div>
          </Link>
        ) : (
          <Link
            to={itemUrl}
            className='tw-flex tw-h-[240px] tw-items-center tw-justify-center tw-no-underline'>
            <WebImage
              webImage={activeItem?.details?.webImages.default.active ?? item.webImages.default.active}
              alt={displayName}
              className='tw-max-h-full tw-max-w-full'
            />
          </Link>
        )}
      </div>

      {!!discountBanner?.primaryText && (
        <Link
          to={itemUrl}
          className={classNames(
            'tw-my-4 tw-block tw-bg-patriot-red tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-white tw-no-underline',
            { '-tw-mx-[17px]': siteId === 2 }
          )}
          style={{
            color: discountBanner.textColor,
            backgroundColor: discountBanner.backgroundColor,
          }}>
          <div className='tw-font-bold'>{discountBanner.primaryText}</div>
          {!!discountBanner.secondaryText && (
            <div className='tw-mt-1 tw-font-normal'>{discountBanner.secondaryText}</div>
          )}
        </Link>
      )}

      <Link to={itemUrl} className='tw-flex tw-justify-between tw-gap-8 tw-no-underline'>
        <p className='tw-mb-0 tw-text-base tw-font-bold tw-uppercase tw-leading-tight tw-text-brand-black'>
          {displayName}
        </p>
      </Link>

      {!item.isBundle && (
        <StarRating
          itemName={displayName}
          reviewRating={rating}
          reviewCount={reviewCount}
          showReviewCount
          starsColor='var(--brand-black)'
          fontSize='14px'
          starsWrapperClassName='tw-text-left'
          labelClassName='!tw-text-brand-black tw-text-xs'
          linkToReviews={false}
        />
      )}

      <Link to={itemUrl} className='tw-block tw-text-left tw-no-underline'>
        {isDiscounted && itemOnSale ? (
          <>
            <p className='tw-mb-0 tw-text-base tw-font-bold tw-text-patriot-red lg:tw-text-lg'>
              Now {currentRateFormatted}
            </p>
            <p className='tw-mb-0 tw-text-base tw-font-normal tw-text-brand-black tw-line-through lg:tw-text-lg'>
              {ourPriceFormatted}
            </p>
          </>
        ) : (
          <p className='tw-mb-1 tw-text-base tw-font-bold tw-text-brand-black'>{ourPriceFormatted}</p>
        )}
      </Link>

      {item.pickerVariants.sizes.length > 1 && (
        <div className='tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-justify-start tw-gap-2.5'>
          {item.pickerVariants.sizes.map((size) => (
            <Link
              key={size.url}
              to={size.url || '#'}
              onClick={onSizeSwatchClick(size)}
              className={cx(
                'after:tw-content-[" "] tw-text-sm tw-font-normal tw-uppercase tw-text-muted tw-underline after:tw-relative after:tw-top-[3px] after:tw-ml-2.5 after:tw-inline-block after:tw-h-[14px] after:tw-w-[2px] after:tw-bg-brand-black last:after:tw-hidden',
                {
                  'tw-text-brand-black': size.id === activeSize?.id,
                }
              )}>
              {size.formattedValue}
            </Link>
          ))}
        </div>
      )}

      {sortedColors.length >= swatchesSliderThreshold && (
        <div className='tw-mt-6 tw-flex tw-items-center'>
          <ColorSwatchStyles colors={sortedColors} />
          <span
            className='tw-inline-flex tw-h-[32px] tw-w-8 tw-shrink-0 tw-grow-0 tw-cursor-pointer tw-items-center tw-justify-center'
            onClick={() => {
              sliderRef.current?.slickPrev();
            }}>
            <BsArrowLeft size={20} />
          </span>

          <Slider
            {...{
              ref: sliderRef,
              variableWidth: true,
              className: 'tw-items-center tw-flex tw-gap-4 tw-w-[calc(100%-80px)] tw-grow',
              arrows: false,
              infinite: true,
              slidesToScroll: swatchesSliderSlidesToScroll,
            }}>
            {repeatedColors.map((color, index) => (
              <div className='tw-h-10 tw-p-1' key={color.id}>
                <ColorSwatch
                  color={color}
                  ignoreDisabledStyle
                  size='sm'
                  itemHandler={onSliderSwatchClick(index)}
                  isActive={color.id === activeColor?.id}
                />
              </div>
            ))}
          </Slider>

          <span
            className='tw-inline-flex tw-h-[32px] tw-w-8 tw-shrink-0 tw-grow-0 tw-cursor-pointer tw-items-center tw-justify-center'
            onClick={() => {
              sliderRef.current?.slickNext();
            }}>
            <BsArrowRight size={20} />
          </span>
        </div>
      )}

      {sortedColors.length < swatchesSliderThreshold && (
        <div className='tw-mb-2.5 tw-mt-4 tw-flex tw-justify-center'>
          <ColorSwatches
            swatchSize='sm'
            showHeader={false}
            colors={sortedColors.slice(0, maxSwatchesShown)}
            activeColor={activeColor}
            itemHandler={onSwatchClick}
            wrapperClassName='tw-text-left'
            colorPickerClassName='!tw-w-full'
            indicateLimitedEdition={false}
            indicateClearance={false}
            swatchesAddendum={
              sortedColors.length > maxSwatchesShown && (
                <p
                  role='link'
                  onClick={() => {
                    window.location.href = itemUrl;
                  }}
                  className='tw-mb-0 tw-inline-flex tw-items-center tw-justify-center tw-text-base tw-leading-[30px]'>
                  +{sortedColors.length - maxSwatchesShown}
                </p>
              )
            }
          />
        </div>
      )}

      {features && features.length > 0 && (
        <div className='tw-mt-4'>
          <ProductCardFeatures features={features} />
        </div>
      )}
    </div>
  );
};

export default ProductCard;

const getColorFromUrl = (url: string) => {
  const urlParts = url.split('?');
  const searchParams = new URLSearchParams(urlParts?.[1] || '');

  return searchParams.get('color');
};
